import { Description, Tune } from '@mui/icons-material';
import {
  Autocomplete,
  AutocompleteRenderInputParams,
  Box,
  DialogActions,
  DialogContent,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import { createWriteOff, getAvailableDocuments } from 'lib/api/writeOffs';
import { AvailableDocument } from 'lib/api/writeOffs/types';
import { DateInput } from 'lib/common/Inputs';
import CustomButton from 'lib/designSystem/Button';
import TableCard from 'lib/designSystem/Card/TableCard';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import SmallHeaderWithIcon from 'lib/designSystem/SmallHeaderWithIcon';
import { formatDate, formatMoney, showAlert } from 'lib/functions/utils';
import { SummaryRow } from 'pages/Finance/Components/Financing/common/Summary';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

interface CreateWriteOffProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const CreateWriteOff: React.FC<CreateWriteOffProps> = ({ open, setOpen }) => {
  const dispatch = useDispatch();
  const [selectedDocument, setSelectedDocument] =
    useState<AvailableDocument | null>(null);
  const newWriteOffAt = new Date(new Date().setUTCHours(12, 0, 0, 0));
  const [writeOffAt, setWriteOffAt] = useState<Date>(newWriteOffAt);
  const { data, isLoading } = getAvailableDocuments(dispatch);
  const availableDocuments = data?.documents || [];

  const { mutate: createWriteOffMutation, isPending: isCreatingWriteOff } =
    createWriteOff(dispatch);

  const disabledCreate = !selectedDocument || isLoading || isCreatingWriteOff;

  const validParams = () => {
    if (!selectedDocument?.id) {
      showAlert(dispatch, 'error', 'Debes seleccionar un documento');
      return false;
    }
    if (!writeOffAt) {
      showAlert(dispatch, 'error', 'Debes seleccionar una fecha de castigo');
      return false;
    }
    return true;
  };

  const handleCreate = async () => {
    if (!validParams()) return;
    createWriteOffMutation(
      {
        documentId: selectedDocument!.id,
        writeOffAt: writeOffAt,
      },
      {
        onSuccess: () => {
          showAlert(dispatch, 'success', 'Castigo creado exitosamente');
          setOpen(false);
        },
        onError: () => {
          showAlert(
            dispatch,
            'error',
            'Ha habido un error al crear el castigo',
          );
        },
      },
    );
  };

  return (
    <div>
      <Dialog open={open} maxWidth="lg" fullWidth>
        <DialogTitle>Crear castigo</DialogTitle>
        <DialogContent>
          {isLoading || isCreatingWriteOff ? (
            <div style={{ minHeight: 100 }}>
              <CenteredCircularProgress type="dialog" />
            </div>
          ) : (
            <Box>
              <TableCard
                size="small"
                enabledEffects={false}
                sx={{
                  boxShadow: 'none',
                  background: 'transparent',
                  border: 'none',
                }}
              >
                <Typography
                  variant="body2"
                  color="textSecondary"
                  style={{ marginBottom: 16 }}
                >
                  Selecciona el documento que quieres castigar.
                </Typography>
                <Autocomplete
                  options={availableDocuments}
                  value={selectedDocument}
                  onChange={(event, newValue) => setSelectedDocument(newValue)}
                  getOptionLabel={(option: AvailableDocument) =>
                    `${option.folio} | ${option.businessName} | ${option.stakeholderName}`
                  }
                  renderInput={(params: AutocompleteRenderInputParams) => (
                    <TextField
                      {...params}
                      label="Busca documentos por folio, emisor o deudor"
                      placeholder="Selecciona un documento..."
                    />
                  )}
                  filterSelectedOptions
                  fullWidth
                  sx={{ marginBottom: 2 }}
                />
                {selectedDocument && (
                  <>
                    <SmallHeaderWithIcon
                      icon={<Description fontSize="small" />}
                      title="Datos del documento"
                    />
                    <Grid container spacing={0} sx={{ mb: 2 }}>
                      <Grid item xs={6}>
                        <Box sx={{ pr: 2 }}>
                          <SummaryRow
                            label="Tipo documento"
                            value={selectedDocument.typeEsp}
                            labelVariant="body2"
                            valueVariant="body2"
                          />
                          <SummaryRow
                            label="Folio"
                            value={selectedDocument.folio}
                            labelVariant="body2"
                            valueVariant="body2"
                          />
                          <SummaryRow
                            label="Monto documento"
                            value={formatMoney(selectedDocument.amount)}
                            labelVariant="body2"
                            valueVariant="body2"
                          />
                          <SummaryRow
                            label="Saldo financiado"
                            value={formatMoney(
                              selectedDocument.documentRemainingAmountNet,
                            )}
                            labelVariant="body2"
                            valueVariant="body2"
                            showBorder={false}
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box
                          sx={{
                            pl: 2,
                            borderLeft: '1px solid',
                            borderColor: 'divider',
                          }}
                        >
                          <SummaryRow
                            label="Emisor"
                            value={selectedDocument.businessName}
                            labelVariant="body2"
                            valueVariant="body2"
                            valueMaxLength={40}
                          />
                          <SummaryRow
                            label="Rut emisor"
                            value={selectedDocument.businessIdentifier}
                            labelVariant="body2"
                            valueVariant="body2"
                          />
                          <SummaryRow
                            label="Deudor"
                            value={selectedDocument.stakeholderName}
                            labelVariant="body2"
                            valueVariant="body2"
                            valueMaxLength={40}
                          />
                          <SummaryRow
                            label="Rut deudor"
                            value={selectedDocument.stakeholderIdentifier}
                            labelVariant="body2"
                            valueVariant="body2"
                            showBorder={false}
                          />
                        </Box>
                      </Grid>
                    </Grid>

                    <SmallHeaderWithIcon
                      icon={<Tune fontSize="small" />}
                      title="Parámetros"
                    />
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 20,
                      }}
                    >
                      <DateInput
                        label="Fecha de castigo"
                        value={formatDate(writeOffAt)}
                        disabled={isLoading}
                        onChange={(value) =>
                          setWriteOffAt(
                            new Date(new Date(value).setUTCHours(12, 0, 0, 0)),
                          )
                        }
                      />
                    </div>
                  </>
                )}
              </TableCard>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <CustomButton
            color="secondary"
            onClick={() => setOpen(false)}
            disabled={isCreatingWriteOff}
          >
            Cancelar
          </CustomButton>
          <CustomButton
            color="primary"
            onClick={handleCreate}
            disabled={disabledCreate}
          >
            Crear
          </CustomButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CreateWriteOff;
